<script setup lang="ts">
import { onMounted, ref } from 'vue';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionUserInfo from '@/application/shared/infrastructure/components/questions/QuestionUserInfo.vue';
import QuestionButtonSet from '@/application/shared/infrastructure/components/questionTypes/QuestionButtonSet.vue';
import QuestionEligibleMedicare from '@/application/shared/infrastructure/components/questions/QuestionEligibleMedicare.vue';
import { useField, useForm } from 'vee-validate';
import { DentalAndVisionApplicant } from '@/application/shared/domain/DentalAndVisionApplicant';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { type Applicant } from '@/application/shared/domain/Applicant';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import { MARITAL_STATUSES } from '@hfc/common/src/types/MaritalStatuses';
import { MEDICARE_STATUSES } from '@hfc/common/src/types/MedicareStatuses';
import { dateHelpers } from '@hfc/ui-core';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import { useQuoteStore } from '@/quote/infrastructure/composition/useQuoteStore';

const { quoteStore } = useQuoteStore();

const { fromTimestampToFormatted } = dateHelpers();
const { getRouteNameByAppType } = useUrlHelper();
const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

interface emitInterface {
  (event: 'goToSection', value: number): void
  (event: 'setLoading', value: boolean): void
}

defineEmits<emitInterface>();

const props = withDefaults(defineProps<{
  applicationType: AppTypeDentalOrVision;
  allowQuoteEdit?: boolean;
  obfuscateSsn?: boolean;
}>(), {
  allowQuoteEdit: true,
  obfuscateSsn: true
});

const {
  dentalAndVisionApplicationStore,
  primaryApplicant,
  isChildOnlyApplication,
  application
} = useDentalAndVisionApplication(props.applicationType);

const quoteRouteName = ref<string>('');

const sectionRef = ref<InstanceType<typeof QuestionUserInfo> | null>(null);

const maritalStatuses = [{
  text: 'Yes',
  value: MARITAL_STATUSES.MARRIED
}, {
  text: 'No',
  value: MARITAL_STATUSES.NOT_MARRIED
}, {
  text: 'Domestic Partnership',
  value: MARITAL_STATUSES.DOMESTIC_PARTNERSHIP
}];

const castedApplicants = application.value.applicants as Array<Applicant>;

const {
  value: maritalStatus,
  errorMessage: maritalStatusError
} = useField('maritalStatus', { required: true }, { initialValue: primaryApplicant.value.marital_status });

const {
  value: eligibleMedicare,
  errorMessage: eligibleMedicareError
} = useField('eligibleMedicare', { required: true }, { initialValue: primaryApplicant.value.eligible_medicare });

const setUserInfoValue = (value: Partial<DentalAndVisionApplicant>): void => {
  dentalAndVisionApplicationStore.updateApplicant(value, props.applicationType, primaryApplicant.value.main_index);
};

const updateMaritalStatus = (value?: string): void => {
  dentalAndVisionApplicationStore.updateApplicant({ ...primaryApplicant.value, marital_status: value as MARITAL_STATUSES }, props.applicationType, primaryApplicant.value.main_index);
};

const updateEligibleMedicare = (value?: string): void => {
  dentalAndVisionApplicationStore.updateApplicant({ ...primaryApplicant.value, eligible_medicare: value as MEDICARE_STATUSES }, props.applicationType, primaryApplicant.value.main_index);
};

const sectionSubmit = async (): Promise<boolean> => {
  const localValid = await runValidations();

  return Boolean(await sectionRef.value?.handleSubmit() && localValid);
};

defineExpose({
  sectionSubmit: sectionSubmit
});

onMounted(async () => {
  const response = await quoteStore.getQuoteInfo(props.applicationType as AppTypeDentalOrVision);
  if (response.success && response.data) {
    const quote = response.data;
    quoteRouteName.value = getRouteNameByAppType(props.applicationType, quote.isSenior ? 'QuoteSenior' : 'Quote');
  }
});

</script>

<template>
  <BaseSection title="Primary Applicant">
    <div class="flex mb-1 justify-between">
      <div class="flex gap-14 italic text-gray-500 text-lg ">
        <div>Gender: <span class="capitalize">{{ primaryApplicant.gender }}</span></div>
        <div>DOB: {{ fromTimestampToFormatted(primaryApplicant.date_of_birth, 'MM/DD/YYYY') }}</div>
        <pre>Zip: {{ primaryApplicant.main_address?.zip_code }}</pre>
      </div>
      <div>
        <router-link v-if="allowQuoteEdit" class="font-medium" :to="{ name: quoteRouteName }">
          Edit Quote Info
        </router-link>
      </div>
    </div>
    <QuestionUserInfo :value="primaryApplicant" ref="sectionRef" @update:value="setUserInfoValue" :auto-focus="true" :hide-suffix="true" :obfuscate-ssn="obfuscateSsn" :all-applicants="castedApplicants" />
    <div class="flex-1">
      <QuestionButtonSet :options="maritalStatuses" v-model="maritalStatus" @update:model-value="updateMaritalStatus" id="maritalStatus" name="maritalStatus" question-text="Are you married?" />
      <span class="error">{{ maritalStatusError }}</span>
    </div>
    <div class="flex-1" v-if="!isChildOnlyApplication">
      <QuestionEligibleMedicare @update:model-value="updateEligibleMedicare" v-model="eligibleMedicare" name="eligibleMedicare" />
      <span class="error">{{ eligibleMedicareError }}</span>
    </div>
  </BaseSection>
</template>
